import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { axisLabel, axisMargin, legendStyle } from './chart-utils';
import { CategoryTick, categoryAxis } from './axis-ticks';

interface LineChartProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  xAxisKey: string;
  height?: number;
  unit?: string;
  /** Achsentitel/Einheit der X-Achse (z.B. "Jahr", "Monat"). */
  xUnit?: string;
  /** Achsentitel/Einheit der Y-Achse (z.B. "%", "Mio."). */
  yUnit?: string;
  className?: string;
  ariaLabel?: string;
  curved?: boolean;
  dots?: boolean;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * LineChart - Liniendiagramm mit optionalen Punkten und Kurven.
 *
 * @example Einfach:
 * data={[{ monat: 'Jan', kunden: 1200 }]}
 * config={{ kunden: { label: 'Kunden', color: '...' } }}
 *
 * @example Mehrere Linien (genestete Daten):
 * data={[{ monat: 'Jan', privat: 1200, gewerbe: 340, industrie: 85 }]}
 * config={{ privat: {...}, gewerbe: {...}, industrie: {...} }}
 */
export function CPLineChart({
  data,
  config,
  xAxisKey,
  height = 350,
  unit,
  xUnit,
  yUnit,
  className,
  ariaLabel,
  curved = true,
  dots = true,
  decimals,
}: LineChartProps) {
  const dataKeys = Object.keys(config);
  const valueLabel = yUnit ?? unit;

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      {(containerWidth) => {
        const { angled, height: categoryAxisHeight } = categoryAxis({ data, xAxisKey, containerWidth, xUnit });
        return (
          <RechartsLineChart data={data} margin={axisMargin({ xUnit, yUnit: valueLabel })}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-brand-border, #e5e7eb)" />
            <XAxis
              dataKey={xAxisKey}
              interval={0}
              tick={<CategoryTick angled={angled} />}
              height={categoryAxisHeight}
              tickLine={false}
              axisLine={false}
              label={axisLabel(xUnit, 'x')}
            />
            <YAxis
              tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }}
              tickLine={false}
              axisLine={false}
              label={axisLabel(valueLabel, 'y')}
            />
            <Tooltip content={<ChartTooltipContent config={config} unit={unit} decimals={decimals} />} />
            {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
                nach series_key ('value'); null behält die Config-/Editor-Reihenfolge. */}
            <Legend
              itemSorter={null}
              iconType="circle"
              iconSize={8}
              wrapperStyle={legendStyle(xUnit)}
              formatter={(value: string) => config[value]?.label ?? value}
            />
            {dataKeys.map((key) => (
              <Line
                key={key}
                type={curved ? 'monotone' : 'linear'}
                dataKey={key}
                stroke={`var(--color-${key})`}
                strokeWidth={2}
                dot={dots ? { r: 3, fill: `var(--color-${key})` } : false}
                activeDot={{ r: 5 }}
              />
            ))}
          </RechartsLineChart>
        );
      }}
    </ChartContainer>
  );
}
